/** TIMS Risk API — dashboard, per-account assessment, what-if scenarios. */
import { apiGet, apiPost } from './client'

export interface ScanPoint {
  price_change: number
  vol_multiplier: number
  scenario_price: number
  scenario_iv: number
  theoretical_pnl: number
}

export interface PositionRisk {
  symbol: string
  underlying: string
  sec_type: string
  quantity: number
  current_price: number
  current_value: number
  strike: number | null
  expiry: string | null
  right: string | null
  implied_vol: number | null
  delta: number | null
  risk_array: ScanPoint[]
  max_loss: number
  max_loss_scenario: string
  margin_requirement: number
  risk_contribution_pct: number
}

export interface ClassGroupRisk {
  underlying: string
  positions: PositionRisk[]
  aggregated_risk_array: ScanPoint[]
  max_loss: number
  max_loss_scenario: string
  margin_requirement: number
  total_delta: number
  risk_contribution_pct: number
}

export interface RiskAssessment {
  alias: string
  ib_account_id: string
  margin_type: string
  timestamp: string
  portfolio_value: number
  cash_balance: number
  margin_requirement: number | null
  margin_available: number | null
  margin_utilization: number | null
  risk_level: 'LOW' | 'MODERATE' | 'ELEVATED' | 'CRITICAL' | 'N/A'
  max_portfolio_loss: number | null
  worst_scenario: ScanPoint | null
  class_groups: ClassGroupRisk[]
  positions: PositionRisk[]
  total_delta: number
  position_count: number
  skipped_positions: number
  iv_defaulted_count: number
}

export interface RiskDashboardAccount {
  alias: string
  available: boolean
  detail?: string
  ib_account_id?: string
  margin_type?: string
  portfolio_value?: number
  account_value?: number
  margin_utilization?: number | null
  risk_level?: string
  position_count?: number
}

export interface WhatIfChange {
  action: 'ADD' | 'REMOVE' | 'MODIFY'
  symbol: string
  sec_type: 'STK' | 'OPT'
  quantity: number
  strike?: number
  expiry?: string
  right?: 'C' | 'P'
  current_price?: number
  implied_vol?: number
}

export const getRiskDashboard = () =>
  apiGet<{ accounts: RiskDashboardAccount[]; total_value: number; needs_attention: string[] }>(
    '/risk/dashboard', { timeout: 60_000 },
  )

export const getRiskAssessment = (alias: string) =>
  apiGet<RiskAssessment>('/risk/assessment', { params: { alias }, timeout: 60_000 })

export const runWhatIf = (alias: string, changes: WhatIfChange[]) =>
  apiPost<{ current: RiskAssessment; scenario: RiskAssessment; margin_delta: number }>(
    '/risk/what-if', { alias, changes }, { timeout: 60_000 },
  )

// ── per-position margins (IB-anchored) ──────────────────────────────────────

export interface PositionMarginGroup {
  underlying: string
  legs: number
  /** IB's own what-if: initial margin released if the group were closed.
   *  Portfolio context included; groups don't sum exactly to the account
   *  total on PM. Null when the what-if could not run. */
  ib_release_init: number | null
  /** Local TIMS/RegT engine figure — the labeled fallback for BP Used. */
  tims_estimate: number | null
  /** TIMS initial margin of this group alone (other positions dropped).
   *  Never a copy of ib_release_init. Null on engine failure. */
  standalone_init: number | null
  source: 'ib' | 'estimate'
  error?: string
}

export interface PositionMarginsResponse {
  alias: string
  as_of: string
  ttl_seconds: number
  groups: PositionMarginGroup[]
  note: string
}

export const getPositionMargins = (alias: string) =>
  apiGet<PositionMarginsResponse>('/risk/position-margins',
    { params: { alias }, timeout: 60_000 })
